import { AutomateTask, success } from "@iflyrpa/share";
import type { AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { type FetchArticlesParams } from ".";
import { DouyinArticle, errorResponse, FetchArticlesResponse } from "./types";

// API 响应接口定义
// API 响应接口定义（参考《API接口文档》1.3 作品列表接口）
interface DouyinApiWork {
    aweme_id: string;
    desc: string;
    Cover?: {
        url_list?: string[];
    };
    create_time: number;
    duration?: number;
    aweme_type?: number;
    status?: {
        is_delete?: boolean;
        is_private?: boolean;
        in_reviewing?: boolean;
    };
    statistics?: {
        play_count?: number;
        digg_count?: number;
        comment_count?: number;
        share_count?: number;
        collect_count?: number;
    };
    share_url?: string;
    author?: {
        uid?: string;
    };
}

interface DouyinWorkListResponse {
    aweme_list?: DouyinApiWork[];
    total?: number;
    has_more?: boolean | number; // 文档为 1/0，兼容 boolean
    cursor?: number; // 文档：下一页游标使用响应中的 cursor
    max_cursor?: number; // 兼容旧字段
    status_code?: number;
}

/**
 * 抖音作品数据采集
 *
 * 采集内容：
 * 1. 账号维度数据：粉丝、获赞、近7日数据（需开通数据中心）
 * 2. 作品维度数据：封面图、标题、发布时间、统计数据等
 *
 * 参考小红书实现模式：
 * - 内部封装 fetchWorks 函数
 * - 支持自动分页
 * - 支持代理配置
 * - 支持重试机制
 */
export async function handleDouyinData(
    _task: AutomateTask,
    params: FetchArticlesParams,
): Promise<FetchArticlesResponse> {
    try {
        const { cookies, pageSize = 12, status = 0, showOriginalData = false, onlySuccess = false } = params;
        // pageNum 为 0 或未传时按 1 处理
        const pageNum = params.pageNum || 1;

        // 配置请求头和代理
        const headers = {
            cookie: cookies.map((it) => `${it.name}=${it.value}`).join(";"),
            referer: "https://creator.douyin.com",
        };

        const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.localIP, params.accountId];

        const http = new Http(...args);

        // 内部函数：获取作品列表（参考文档 1.3）
        // 分页使用游标 max_cursor，首次为 0，后续用响应中的 cursor
        async function fetchWorks(cursor: number): Promise<DouyinWorkListResponse> {
            return await http.api<DouyinWorkListResponse>(
                {
                    method: "get",
                    url: "https://creator.douyin.com/janus/douyin/creator/pc/work_list",
                    params: {
                        scene: "star_atlas",
                        device_platform: "android",
                        aid: 1128,
                        status,
                        count: pageSize,
                        max_cursor: cursor,
                    },
                },
                { retries: 3, retryDelay: 100, timeout: 5000 },
            );
        }

        // has_more 兼容文档的 1/0 与 boolean
        const isHasMore = (v: boolean | number | undefined): boolean => v === true || v === 1;

        // 计算需要跳过和获取的数据量
        const skipCount = (pageNum - 1) * pageSize;
        const needCount = skipCount + pageSize;

        // 首次请求游标为 0（抖音使用游标分页，非 offset）
        const workListResponse = await fetchWorks(0);

        if (!workListResponse || !Array.isArray(workListResponse.aweme_list)) {
            return errorResponse("抖音数据获取失败，请检查账号状态", 414);
        }

        // 累积作品，通过游标翻页直到攒够需要的数量或没有更多
        const allWorks: DouyinApiWork[] = [...workListResponse.aweme_list];
        let hasMore = isHasMore(workListResponse.has_more);
        // 文档：后续请求使用响应中的 cursor；兼容旧的 max_cursor
        let currentCursor = workListResponse.cursor ?? workListResponse.max_cursor ?? 0;

        // 防止无限循环的翻页上限
        let guard = 0;
        while (allWorks.length < needCount && hasMore && guard < 20) {
            const nextPageResponse = await fetchWorks(currentCursor);
            if (!Array.isArray(nextPageResponse.aweme_list) || nextPageResponse.aweme_list.length === 0) {
                break;
            }
            allWorks.push(...nextPageResponse.aweme_list);
            hasMore = isHasMore(nextPageResponse.has_more);
            currentCursor = nextPageResponse.cursor ?? nextPageResponse.max_cursor ?? currentCursor;
            guard++;
        }

        // 截取当前页的数据：跳过前面的页，取 pageSize 条
        const finalWorks = allWorks.slice(skipCount, needCount);

        // 转换作品数据（字段映射参考文档 1.3）
        const articleCell: DouyinArticle[] = finalWorks.map((item) => {
            // 作品详情页（创作者后台），参考文档"作品数据页面地址构建"
            const workDetailUrl = `https://creator.douyin.com/creator-micro/work-management/work-detail/${item.aweme_id}`;
            // 公开访问 URL 优先用文档给出的 share_url
            const workPublicUrl = item.share_url || `https://www.douyin.com/video/${item.aweme_id}`;

            // 发布状态：删除 > 私密 > 审核中 > 公开
            let statusText: string;
            if (item.status?.is_delete) {
                statusText = "deleted";
            } else if (item.status?.is_private) {
                statusText = "private";
            } else if (item.status?.in_reviewing) {
                statusText = "reviewing";
            } else {
                statusText = "public";
            }

            return {
                title: item.desc || "无作品描述",
                imageUrl: item.Cover?.url_list?.[0] || "",
                createTime: item.create_time,
                redirectUrl: workDetailUrl,

                // 统计数据
                playNum: item.statistics?.play_count || 0,
                likeNum: item.statistics?.digg_count || 0,
                commentNum: item.statistics?.comment_count || 0,
                shareNum: item.statistics?.share_count || 0,
                collectNum: item.statistics?.collect_count || 0,

                // 扩展字段
                awemeId: item.aweme_id,
                awemeType: item.aweme_type ?? 0,
                duration: item.duration,
                status: statusText,
                publicUrl: workPublicUrl,

                ...(showOriginalData ? { originalData: item } : {}),
            };
        });

        return success(
            {
                articleCell,
                ...(onlySuccess
                    ? {
                          pagination: {
                              total: workListResponse.total || allWorks.length,
                              nextPage: hasMore,
                              pageNum,
                              pageSize,
                          },
                      }
                    : null),
            },
            "抖音数据获取成功",
        );
    } catch (error) {
        return errorResponse(error instanceof Error ? error.message : "抖音数据获取失败");
    }
}
